import { ActivityFeedFilter, ActivityFeedItem, ActivityFeedType } from '@/types/activityFeed';

export const SYSTEM_USER = 'System User';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const ACTIVITY_NOTE_FILTERS: ActivityFeedFilter[] = [ActivityFeedFilter.CLINICAL_NOTES];

export const ACTIVITY_ALL_FILTERS: ActivityFeedFilter[] = [
  ActivityFeedFilter.RED,
  ActivityFeedFilter.YELLOW,
  ActivityFeedFilter.REMOTE,
  ActivityFeedFilter.IN_CLINIC,
  ActivityFeedFilter.INBOUND,
  ActivityFeedFilter.OUTBOUND,
  ActivityFeedFilter.POST_GRID_LETTERS,
  ActivityFeedFilter.PATIENT_STATUS,
  ActivityFeedFilter.CLINICAL_NOTES
];

export function personName(value?: string | null, fallback = SYSTEM_USER): string {
  const name = value?.trim();
  if (!name || UUID.test(name)) return fallback;
  return name;
}

export function isPatientStatusEvent(eventType: string): boolean {
  return eventType === ActivityFeedType.PATIENT_STATUS || eventType === 'status';
}

function statusLabel(status?: string | null): string {
  const value = status?.trim();
  if (!value) return 'Patient status updated';
  return `Patient status · ${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
}

export function statusReasonText(data: ActivityFeedItem['data']): string | undefined {
  const reason = data.statusReason?.trim();
  const other = data.statusOtherReason?.trim();
  if (reason && reason.toLowerCase() !== 'other') return reason;
  return other || reason || undefined;
}

export function statusEventCopy(data: ActivityFeedItem['data']): {
  actor: string;
  detail: string;
  lines?: string[];
} {
  const reason = statusReasonText(data);
  return {
    actor: personName(data.lastUpdatedBy),
    detail: statusLabel(data.patientStatus),
    lines: reason ? [`Reason: ${reason}`] : undefined
  };
}

function retellNote(reason?: string): string | undefined {
  switch (reason) {
    case 'call_transfer':
      return 'Call transferred to device support team for further assistance.';
    case 'voicemail_reached':
      return 'Recipient did not answer call, message left to return call at their earliest convenience.';
    case 'user_hangup':
      return 'Call disconnected by recipient.';
    case 'agent_hangup':
      return 'Unable to leave a voicemail or call disconnected/lost.';
    default:
      return undefined;
  }
}

function disconnectLabel(reason?: string): string | undefined {
  const value = reason?.trim();
  if (!value) return undefined;
  const words = value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  return `Disconnected (${words})`;
}

export function callEventCopy(item: ActivityFeedItem): {
  actor: string;
  detail: string;
  lines?: string[];
  direction?: 'inbound' | 'outbound';
} {
  const retell = item.eventType === ActivityFeedType.RETELL_CALLS;
  const disconnectReason = item.data.disconnect_reason?.trim();
  const notes = item.data.callNotes?.trim() || (retell ? retellNote(disconnectReason) : undefined);
  const disposition =
    item.data.disposition?.trim() || (retell ? disconnectLabel(disconnectReason) : undefined);
  const direction =
    item.data.direction === 'INBOUND'
      ? 'inbound'
      : item.data.direction === 'OUTBOUND'
        ? 'outbound'
        : retell
          ? 'outbound'
          : undefined;
  const lines =
    notes && disposition && disposition !== notes ? [`Disposition: ${disposition}`] : undefined;

  return {
    actor: retell ? 'Agentic AI' : personName(item.data.agentName),
    detail: notes || disposition || item.data.case_type || 'Phone call',
    lines,
    direction
  };
}
